<template>
  <div ref="dropdownRef" class="relative w-full mb-1">
    <label v-if="label" class="block text-13 text-gray-500">{{ label }}</label>
    <div class="relative w-full" :class="{ 'border-b-transparent rounded-t-6': isOpen }">
      <div
        class="relative flex pt-[5px] items-center justify-between w-full h-40 border border-gray-200 rounded-6 bg-white hover:border-primary-600"
        :class="{
          'rounded-b-none': isOpen,
          '!border-red-500': !!hasErrors,
          'cursor-not-allowed opacity-70': disabled,
          'hover:border-gray-200': disabled,
        }"
        @click="openDropdown"
      >
        <input
          ref="inputRef"
          type="text"
          class="flex-1 w-full h-40 border-none px-12 py-8 outline-none text-14 text-gray-900 bg-transparent focus:outline-none focus:ring-0"
          :class="{
            '!border-gray-200 !bg-gray-50 !text-gray-300': disabled,
            'cursor-not-allowed': disabled,
            'pr-56': selectedItems.length > 0,
            'pr-32': selectedItems.length === 0,
          }"
          v-model="searchTerm"
          :placeholder="placeholder"
          @click.stop="openDropdown"
          @input="filterOptions"
          @focus="onInputFocus"
          @blur="onInputBlur"
          :disabled="disabled"
        />
        <div class="absolute right-0 top-0 h-full flex items-center">
          <button
            v-if="selectedItems.length > 0"
            @click.stop="clearAll"
            class="mr-4 p-4 text-gray-400 hover:text-red-500 transition-colors duration-200 rounded-4 flex items-center justify-center"
            :disabled="disabled"
            type="button"
            title="Clear all selections"
          >
            <i class="fas fa-times text-12"></i>
          </button>

          <div
            class="pr-12 flex items-center justify-center transition-transform duration-200"
            @click.stop="toggleDropdown"
          >
            <i :class="['fas', isOpen ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
          </div>
        </div>
      </div>

      <div
        v-show="isOpen"
        class="absolute top-full left-0 w-full max-h-200 overflow-y-auto bg-white border border-gray-200 border-t-0 rounded-b-6 z-10 shadow-lg"
      >
        <div
          v-for="option in filteredOptions"
          :key="option.value"
          class="px-12 py-8 cursor-pointer text-14 min-h-32 flex items-center hover:bg-primary-50"
          @click.stop="toggleOption(option.value)"
        >
          <input
            :id="`${name}-${option.value}`"
            :checked="selectedItems.includes(option.value)"
            type="checkbox"
            :disabled="disabled"
            class="mr-8 rounded-6 h-16 w-16 [&:checked]:bg-primary-500 [&:checked]:border-primary-500 [&:active]:bg-primary-600 [&:hover]:border-primary-400 [&:focus]:ring-2 [&:focus]:ring-primary-500 [&:focus]:ring-offset-2 [&:focus]:outline-none pointer-events-none"
            style="accent-color: #468641"
            @click.stop
          />
          <span>{{ option.label }}</span>
        </div>

        <div
          v-if="filteredOptions.length === 0 && searchTerm !== ''"
          class="px-12 py-8 text-gray-500 text-14 min-h-32 flex items-center justify-center italic"
        >
          No results found for "{{ searchTerm }}"
        </div>

        <div
          v-if="sortedOptions.length === 0"
          class="px-12 py-8 text-gray-500 text-14 min-h-32 flex items-center justify-center italic"
        >
          No options available
        </div>
      </div>
    </div>

    <div v-if="error" class="min-h-16">
      <span class="text-11 text-red-500 block mt-2" v-text="error" />
    </div>
  </div>
</template>

<script>
import { ref, watch } from 'vue';

export default {
  name: 'SparkMultiFilter',

  props: {
    name: { type: String, required: true },
    label: { type: String, default: '' },
    placeholder: { type: String, default: 'Select options' },
    options: { type: Array, default: () => [] },
    modelValue: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    error: { type: String, default: '' },
  },

  emits: ['update:modelValue', 'change'],

  data() {
    return {
      isOpen: false,
      selectedItems: [...(this.modelValue || [])],
      internalUpdate: false,
      searchTerm: '',
      filteredOptions: [],
      isFocused: false,
    };
  },

  setup(props, { emit }) {
    const hasErrors = ref(false);
    watch(
      () => props.error,
      () => {
        hasErrors.value = props.error ? true : false;
      },
      { immediate: true }
    );

    return {
      hasErrors,
    };
  },

  computed: {
    sortedOptions() {
      return [...this.options].sort((a, b) => a.label.localeCompare(b.label));
    },

    selectedItemsText() {
      if (this.selectedItems.length === 0) {
        return '';
      } else if (this.selectedItems.length === 1) {
        const selectedOption = this.options.find(option => option.value === this.selectedItems[0]);
        return selectedOption?.label || '';
      } else {
        const firstOption = this.options.find(option => option.value === this.selectedItems[0]);
        const remainingCount = this.selectedItems.length - 1;
        return `${firstOption?.label || ''}, +${remainingCount} more`;
      }
    },
  },

  watch: {
    selectedItems: {
      handler(newVal) {
        this.internalUpdate = true;
        this.$emit('update:modelValue', newVal);
        this.$emit('change', newVal);
        this.$nextTick(() => {
          this.internalUpdate = false;
        });
        // Update input display when not focused
        if (!this.isFocused) {
          this.updateInputDisplay();
        }
      },
      deep: true,
    },

    modelValue: {
      handler(newVal) {
        if (!this.internalUpdate && JSON.stringify(newVal) !== JSON.stringify(this.selectedItems)) {
          this.selectedItems = [...(newVal || [])];
        }
      },
      deep: true,
    },

    options: {
      handler() {
        this.updateFilteredOptions();
      },
      immediate: true,
    },

    searchTerm() {
      this.updateFilteredOptions();
    },
  },

  mounted() {
    document.addEventListener('click', this.handleClickOutside);
    this.updateFilteredOptions();
    this.updateInputDisplay();
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },

  methods: {
    handleClickOutside(event) {
      if (this.$refs.dropdownRef && !this.$refs.dropdownRef.contains(event.target)) {
        this.closeDropdown();
      }
    },

    openDropdown() {
      if (this.disabled) return;
      if (!this.isOpen) {
        this.isOpen = true;
        this.$nextTick(() => {
          if (this.$refs.inputRef) {
            this.$refs.inputRef.focus();
          }
        });
      }
    },

    toggleDropdown() {
      if (this.disabled) return;
      if (this.isOpen) {
        this.closeDropdown();
      } else {
        this.openDropdown();
      }
    },

    onInputFocus() {
      this.isFocused = true;
      this.isOpen = true;
      // Clear the input to allow typing
      this.searchTerm = '';
    },

    onInputBlur(event) {
      // For multi-select, we don't want to close on blur when clicking options
      // The handleClickOutside method will handle closing when clicking outside
      setTimeout(() => {
        this.isFocused = false;
        // Don't automatically close on blur - let handleClickOutside handle it
        this.updateInputDisplay();
      }, 150);
    },

    toggleOption(value) {
      if (this.disabled) return;

      const index = this.selectedItems.indexOf(value);
      if (index > -1) {
        this.selectedItems.splice(index, 1);
      } else {
        this.selectedItems.push(value);
      }
    },

    closeDropdown() {
      this.isOpen = false;
      this.updateInputDisplay();
    },

    updateInputDisplay() {
      if (!this.isFocused) {
        this.searchTerm = this.selectedItemsText;
      }
    },

    updateFilteredOptions() {
      if (this.searchTerm === '' || !this.isFocused) {
        this.filteredOptions = [...this.sortedOptions].filter(
          option => option.label !== '' && option.value !== undefined && option.value !== null
        );
      } else {
        const searchTermLower = this.searchTerm.toLowerCase();
        this.filteredOptions = this.sortedOptions.filter(
          option =>
            option.label !== '' &&
            option.value !== undefined &&
            option.value !== null &&
            option.label.toLowerCase().includes(searchTermLower)
        );
      }
    },

    filterOptions() {
      this.updateFilteredOptions();
    },

    clearAll() {
      this.selectedItems = [];
      this.searchTerm = '';
      this.updateFilteredOptions();
      this.updateInputDisplay();
    },
  },
};
</script>
